import { mockVtubers } from "../data/mockVtubers";

const MOCK_DELAY_MS = 300;

const ATTRIBUTE_FILTER_KEYS = [
  "genre",
  "affiliation", //個人企業
  "hairColor",
  "eyeColor",
  "hairLength", //髪長
  "accessory", //アクセサリー
  "voicePitch",
  "voiceCharacter", //声の特徴
  // "gameGenre", //ゲームジャンル
  "talkStyle",
  "personality", //性格
  // "streamStyle",
  // "streamTime",
  // "streamFrequency",
  "language",
  "model",
];

export const SUBSCRIBER_OPTIONS = [
  {
    value: "subscriber_under_1k",
    label: "1,000人未満",
    min: 0,
    max: 999,
  },
  {
    value: "subscriber_1k_5k",
    label: "1,000〜5,000人",
    min: 1000,
    max: 4999,
  },
  {
    value: "subscriber_5k_10k",
    label: "5,000〜1万人",
    min: 5000,
    max: 9999,
  },
  {
    value: "subscriber_10k_50k",
    label: "1万〜5万人",
    min: 10000,
    max: 49999,
  },
  {
    value: "subscriber_50k_100k",
    label: "5万〜10万人",
    min: 50000,
    max: 99999,
  },
  {
    value: "subscriber_100k_500k",
    label: "10万〜50万人",
    min: 100000,
    max: 499999,
  },
  {
    value: "subscriber_500k_1m",
    label: "50万〜100万人",
    min: 500000,
    max: 999999,
  },
  {
    value: "subscriber_1m_plus",
    label: "100万人以上",
    min: 1000000,
    max: Infinity,
  },
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const DAY_MAP = [
  "day_sunday",
  "day_monday",
  "day_tuesday",
  "day_wednesday",
  "day_thursday",
  "day_friday",
  "day_saturday",
];

const STREAM_TIME_RANGES = {
  stream_time_early_morning: [5, 8],
  stream_time_morning: [8, 12],
  stream_time_afternoon: [12, 17],
  stream_time_evening: [17, 20],
  stream_time_night: [20, 24],
  stream_time_midnight: [0, 5],
};

function daySearcher(videos) {
  return videos.reduce((result, video) => {
    const day = DAY_MAP[new Date(video.startedAt).getDay()];

    result[day] = (result[day] ?? 0) + 1;

    return result;
  }, {});
}

function gameGenreSearcher(videos) {
  return videos.reduce((result, video) => {
    video.attributes
      .filter((attribute) => attribute.startsWith("game_"))
      .forEach((genre) => {
        result[genre] = (result[genre] ?? 0) + 1;
      });

    return result;
  }, {});
}

function streamTimeSearcher(videos) {
  return videos.reduce((result, video) => {
    const hour = new Date(video.startedAt).getHours();

    Object.entries(STREAM_TIME_RANGES).forEach(([timeKey, [minHour, maxHour]]) => {
      if (hour >= minHour && hour < maxHour) {
        result[timeKey] = (result[timeKey] ?? 0) + 1;
      }
    });

    return result;
  }, {});
}

function streamFrequencyAttribute(videos) {
  const perWeek = videos.length / 13;

  if (perWeek >= 6) return "stream_frequency_daily";
  if (perWeek >= 4) return "stream_frequency_high";
  if (perWeek >= 2) return "stream_frequency_middle";
  if (perWeek >= 0.75) return "stream_frequency_low";
  if (perWeek > 0) return "stream_frequency_rare";
  return "stream_frequency_irregular";
}

// 検索条件（配信曜日・ゲームジャンル・配信時間帯・配信頻度）の集計ロジックを
// そのまま使い、実際の配信実績から属性を導出する。
function deriveAttributesFromVideos(videos) {
  const derived = [];

  Object.entries(daySearcher(videos)).forEach(([day, count]) => {
    if (count >= 6) derived.push(day);
  });

  Object.entries(gameGenreSearcher(videos)).forEach(([genre, count]) => {
    if (count >= 6) derived.push(genre);
  });

  Object.entries(streamTimeSearcher(videos)).forEach(([time, count]) => {
    if (count >= 6) derived.push(time);
  });

  derived.push(streamFrequencyAttribute(videos));

  return derived;
}

export const searchVtubers = async (filters = {}) => {
  await wait(MOCK_DELAY_MS);

  return mockVtubers.filter((vtuber) => {
    if (filters.vtuberName && !vtuber.name.includes(filters.vtuberName)) {
      return false;
    }

    const derivedAttributes = deriveAttributesFromVideos(vtuber.videos);

    if (filters.day && !derivedAttributes.includes(filters.day)) {
      return false;
    }

    if (filters.gameGenre && !derivedAttributes.includes(filters.gameGenre)) {
      return false;
    }

    if (filters.streamTime && !derivedAttributes.includes(filters.streamTime)) {
      return false;
    }

    if (
      filters.streamFrequency &&
      !derivedAttributes.includes(filters.streamFrequency)
    ) {
      return false;
    }

    if (
      ATTRIBUTE_FILTER_KEYS.some(
        (key) =>
          filters[key] &&
          !vtuber.attributes.some(
            (attribute) =>
              attribute.value === filters[key] &&
              (filters.includeUnapproved || attribute.approved),
          ),
      )
    ) {
      return false;
    }

    const subscriberOption = SUBSCRIBER_OPTIONS.find(
      (option) => option.value === filters.subscriber,
    );

    if (
      subscriberOption &&
      (vtuber.subscriberCount < subscriberOption.min ||
        vtuber.subscriberCount > subscriberOption.max)
    ) {
      return false;
    }

    return true;
  });
};

export const getVtuberById = async (id) => {
  await wait(MOCK_DELAY_MS);

  const vtuber = mockVtubers.find((v) => v.id === id) || null;

  if (!vtuber) {
    return null;
  }

  const derivedAttributes = deriveAttributesFromVideos(vtuber.videos);

  const mergedAttributes = new Map(
    vtuber.attributes.map((attribute) => [attribute.value, attribute]),
  );

  derivedAttributes.forEach((value) => {
    if (!mergedAttributes.has(value)) {
      mergedAttributes.set(value, { value, approved: true });
    }
  });

  return {
    ...vtuber,
    attributes: Array.from(mergedAttributes.values()),
  };
};

export const TRENDING_RANGE_OPTIONS = [
  { value: 'today', label: '今日' },
  { value: 'week', label: '今週' },
  { value: 'month', label: '今月' },
];

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfWeek(date) {
  const start = startOfDay(date);
  const mondayOffset = (start.getDay() + 6) % 7; // getDay(): 0=日〜6=土 -> 月曜始まりに変換

  start.setDate(start.getDate() - mondayOffset);

  return start;
}

function isSameRange(range, startedAt, now) {
  if (range === "today") {
    return startOfDay(startedAt).getTime() === startOfDay(now).getTime();
  }

  if (range === "week") {
    return startOfWeek(startedAt).getTime() === startOfWeek(now).getTime();
  }

  return (
    startedAt.getFullYear() === now.getFullYear() &&
    startedAt.getMonth() === now.getMonth()
  );
}

export const getTrendingVideos = (range = "today", now = new Date()) => {
  return mockVtubers
    .flatMap((vtuber) =>
      vtuber.videos.map((video) => ({
        ...video,
        vtuberId: vtuber.id,
        vtuberName: vtuber.name,
        vtuberIconUrl: vtuber.iconUrl,
      })),
    )
    .filter((video) => isSameRange(range, new Date(video.startedAt), now))
    .sort((a, b) => b.score - a.score);
};
